import React from "react";

import {
  faBriefcase,
  faEdit,
  faExclamationCircle,
  faPlus,
  faTable,
} from "@fortawesome/pro-regular-svg-icons";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { groupBy, times } from "lodash-es";
import { useNavigate } from "react-router-dom";

import { DocumentTitle } from "src/base-components/DocumentTitle";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import {
  ResourceCard,
  ResourceCardSkeleton,
  ResourceCardsContainer,
} from "src/base-components/ResourceCard";
import {
  ResourceStatusPill,
  ResourceStatusPillContainer,
} from "src/base-components/ResourceStatusPill";
import { CasePageLayout } from "src/cases/CasePageLayout";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseTemplateModal } from "src/cases/templates/CaseTemplateModal";
import { CreateFromExistingTemplateModal } from "src/cases/templates/CreateFromExistingTemplateModal";
import { useAddTemplateDisabledReason } from "src/cases/templates/useAddTemplateDisabledReason";
import { getTemplateIcon } from "src/cases/templates/utils";
import {
  CaseTemplateVersionPublishState,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { useCapabilities } from "src/hooks/useCapabilities";
import { WhitePane } from "src/jobs/common/WhitePane";
import {
  DashboardPageParams,
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CaseTemplatesList: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const {
    data: caseTemplates,
    isError,
    isLoading,
    refetch,
    isFetching,
  } = useCaseTemplatesWithVersions();
  const navigate = useNavigate();
  const {
    isOpen,
    openModal,
    closeModal,
    data: templateIdToEdit,
  } = useModal<string>();

  if (!isLoading && isError) {
    return (
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            label: "Retry",
            onClick: () => refetch(),
            isDisabled: isFetching,
          }}
          dataLoc="case-templates-error-state"
          description="We're having trouble loading case templates. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading case templates"
          icon={faExclamationCircle}
          variant="error"
        />
      </WhitePane>
    );
  }

  if (!isLoading && (!caseTemplates || caseTemplates.length === 0)) {
    return (
      <WhitePane>
        <EmptyState
          dataLoc="case-templates-empty-state"
          description="There are no templates found in this workspace yet."
          headline="No templates found"
          icon={faBriefcase}
        />
      </WhitePane>
    );
  }

  return (
    <>
      <ResourceCardsContainer>
        {isLoading
          ? times(3, (i) => <ResourceCardSkeleton key={i} />)
          : caseTemplates?.map((template) => (
              <ResourceCard
                key={template.id}
                dataLoc={`template-card-${template.id}`}
                description={template.attributes.description}
                title={
                  <div className="flex items-start gap-1.5">
                    <Icon
                      icon={
                        getTemplateIcon(template.attributes.icon) ?? faTable
                      }
                      size="xs"
                    />
                    <div className="line-clamp-2">
                      {template.attributes.template_name}
                    </div>
                  </div>
                }
                onClick={() => {
                  navigate(
                    getUrlToCaseTemplateVersionsPage(orgId, wsId, template.id),
                  );
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <TemplateVersionsPills versions={template.versions} />
                  <EllipsisOptionsDropdown
                    buttonClassName="opacity-0 group-hover/resource-card:opacity-100 w-6 h-6"
                    dataLoc="template-actions"
                    elements={[
                      {
                        key: "Edit details",
                        icon: faEdit,
                        action: () => openModal(template.id),
                      },
                    ]}
                    placement="bottom start"
                  />
                </div>
              </ResourceCard>
            ))}
      </ResourceCardsContainer>
      <CaseTemplateModal
        open={isOpen}
        templateId={templateIdToEdit}
        onClose={closeModal}
      />
    </>
  );
};

type TemplateVersionsPillsProps = {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

const TemplateVersionsPills: React.FC<TemplateVersionsPillsProps> = ({
  versions,
}) => {
  const versionsByState = groupBy(versions, "attributes.publish_state");

  return (
    <ResourceStatusPillContainer>
      {Object.entries(versionsByState).map(([state, versions]) => (
        <ResourceStatusPill
          key={state}
          color={
            state === CaseTemplateVersionPublishState.PUBLISHED
              ? "green"
              : "yellow"
          }
          tooltip={{
            title: pluralize(
              versions.length,
              state === CaseTemplateVersionPublishState.PUBLISHED
                ? "published version"
                : "draft version",
            ),
          }}
          value={versions.length}
        />
      ))}
    </ResourceStatusPillContainer>
  );
};

export const CaseTemplatesPage: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { caseTemplates } = useCapabilities();

  const canCreateTemplate = caseTemplates.canCreate;
  const addTemplateDisabledReason = useAddTemplateDisabledReason();

  const createModal = useModal();
  const fromTemplateModal = useModal();

  return (
    <CasePageLayout
      breadcrumbLabel="Case Templates"
      breadcrumbHref={getUrlToCaseTemplatesPage(orgId, wsId)}
      headerActions={
        canCreateTemplate ? (
          <OptionsDropdown
            disabled={Boolean(addTemplateDisabledReason)}
            elements={[
              {
                key: "From scratch",
                action: createModal.openModal,
              },
              {
                key: "From a template",
                action: fromTemplateModal.openModal,
              },
            ]}
          >
            <div className="pr-4">
              <Tooltip
                body={addTemplateDisabledReason}
                disabled={!addTemplateDisabledReason}
                asChild
              >
                <IconButton
                  aria-label="Create case template"
                  dataLoc="create-case-template"
                  disabled={Boolean(addTemplateDisabledReason)}
                  icon={faPlus}
                  size="xs"
                />
              </Tooltip>
            </div>
          </OptionsDropdown>
        ) : undefined
      }
    >
      <DocumentTitle segments={["Case Templates"]} />
      <div
        className="decideScrollbar h-full min-w-[300px] overflow-hidden"
        data-loc="case-templates-page"
      >
        <CaseTemplatesList />
      </div>

      <CaseTemplateModal
        open={createModal.isOpen}
        onClose={createModal.closeModal}
      />
      <CreateFromExistingTemplateModal
        open={fromTemplateModal.isOpen}
        onClose={fromTemplateModal.closeModal}
      />
    </CasePageLayout>
  );
};
